import { SdkStaff, SingleTagType, TagsType } from 'src/AppState/sdk/staff';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { OpenapiProxyStaffGetMarketTemplatesResponse200Type } from 'src/api_openapi/generated/openapi_proxy_staff_get_market_templates';
import { MarketTemplateSelectionsArrayType } from 'src/components/MarketTemplateSettingsForm/MarketTemplateSettingsForm.state';
import { action, observable, makeAutoObservable } from 'mobx';
import { arrayMove } from 'react-movable';
import { computed } from 'src_common/common/mobx-wrapper';
import { MarketTemplateItem } from 'src/components/SportPopup/SportMarketTemplates/SportMarketTemplatesItem';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';

export type MarketTemplatesType = OpenapiProxyStaffGetMarketTemplatesResponse200Type['results'];
export type PopupType = 'isMarketTemplateOpen' | 'isSelectionTemplateOpen';

const isGeniusSelectionsGuard = createGuard(
    t.array(t.partial({ selectionKind: t.union([t.null, t.partial({ mappings: t.array(t.string) })]) }))
);

// Workaround: the market-level source of truth is `marketKind.mappings` (e.g. "genius:market:10591"),
// but the api-proxy doesn't return it yet, so we infer it from selection mappings in `selectionsNewTrading`.
// Switch to `marketKind` once the proxy exposes it.
const isGeniusTemplate = (selectionsNewTrading: string | null | undefined): boolean => {
    if (typeof selectionsNewTrading !== 'string') {
        return false;
    }
    try {
        const selections: unknown = JSON.parse(selectionsNewTrading);
        return (
            isGeniusSelectionsGuard(selections) &&
            selections.some(
                (selection) =>
                    selection.selectionKind?.mappings?.some((mapping) => mapping.startsWith('genius:')) === true
            )
        );
    } catch {
        return false;
    }
};

export class SportMarketTemplatesState {
    @observable public expendedList: Array<string> = [];
    @observable public openedPopup: PopupType | null = null;
    public readonly templatesResource: Resource<MarketTemplatesType>;
    private readonly tagsResource: Resource<TagsType>;

    public constructor(
        private readonly sportId: string,
        private readonly sdkStaff: SdkStaff
    ) {
        this.templatesResource = new Resource(async () => {
            const sportTemplates = await this.sdkStaff.getMarketTemplates(this.sportId);
            return sportTemplates.results;
        });
        this.tagsResource = new Resource(async (): Promise<TagsType> => {
            return await this.sdkStaff.getAllTags();
        });
        makeAutoObservable(this);
    }

    @computed public get getTags(): SingleTagType | 'loading' {
        const response = this.tagsResource.get();
        if (response.type === 'loading') {
            return 'loading';
        }

        if (response.type === 'ready') {
            const tagsForSport: SingleTagType = {};
            const allTags: SingleTagType = response.value.definitions;
            for (const tagsForSportKey in allTags) {
                if (allTags[tagsForSportKey]?.sports.includes(this.sportId) === true) {
                    const tag = allTags[tagsForSportKey];
                    tagsForSport[tagsForSportKey] = tag;
                }
            }

            return tagsForSport;
        }
        return {};
    }

    @computed public get marketTemplates(): MarketTemplatesType | 'loading' {
        const response = this.templatesResource.get();
        if (response.type === 'ready') {
            return response.value.sort((a, b) => a.displayOrder - b.displayOrder);
        }
        return 'loading';
    }

    @computed public get geniusTemplateKeys(): Set<string> {
        const keys = new Set<string>();
        if (this.marketTemplates === 'loading') {
            return keys;
        }
        for (const template of this.marketTemplates) {
            if (isGeniusTemplate(template.selectionsNewTrading)) {
                keys.add(this.getTemplateKey(template));
            }
        }
        return keys;
    }

    public getTemplateKey = (template: MarketTemplateItem): string => {
        if (template.id.includes('custom')) {
            return template.id;
        }

        return template.matchingMarkets ?? template.id;
    };

    public marketTemplateByIt = (templateId: string): MarketTemplateItem | 'loading' => {
        if (this.marketTemplates === 'loading') {
            return 'loading';
        }

        return (
            this.marketTemplates.find((template) => {
                return this.getTemplateKey(template) === templateId;
            }) ?? 'loading'
        );
    };

    public onSort = async ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): Promise<void> => {
        const updateList: Record<
            string,
            Record<
                string,
                {
                    displayOrder: number;
                    marketTemplateId: string;
                    matchingMarkets: string | undefined;
                }
            >
        > = {};
        const items = this.marketTemplates;
        if (items === 'loading') {
            console.warn('Markets are still loading...');
            return;
        }
        const list = arrayMove(items, oldIndex, newIndex);
        list.map((item, idx) => {
            if (idx !== item.displayOrder) {
                const eventTemplateId = item.platformObject?.eventTemplateId ?? item.eventTemplate?.id;
                // @ts-expect-error
                updateList[eventTemplateId] = {
                    // @ts-expect-error
                    ...updateList[eventTemplateId],
                    [item.id]: {
                        displayOrder: idx,
                        matchingMarkets: item.matchingMarkets,
                    },
                };
            }
        });

        try {
            for (const eventTemplateId in updateList) {
                const body = updateList[eventTemplateId];
                if (body !== undefined) {
                    const response = await this.sdkStaff.patchMarketTemplatesReorder({
                        requestBody: {
                            sportId: this.sportId,
                            eventTemplateId: eventTemplateId,
                            data: body,
                        },
                    });
                    if (response.code !== 200) {
                        break;
                    }
                }
            }
        } catch (error) {
            console.log(error);
        }

        await this.templatesResource.refresh();
    };

    @action public togglePopup = async (popupType: PopupType): Promise<void> => {
        this.openedPopup = this.openedPopup === popupType ? null : popupType;
        if (this.openedPopup === null) {
            // Need subscription model for remove timout
            setTimeout(async () => {
                await this.templatesResource.refresh();
            }, 500);
        }
    };

    @action public toggleTemplate = (template: MarketTemplateSelectionsArrayType): void => {
        const templateId = this.getTemplateKey(template);
        const idx = this.expendedList.indexOf(templateId);
        if (idx === -1) {
            this.expendedList.push(templateId);
        } else {
            this.expendedList.splice(idx, 1);
        }
    };

    @action public onOpenSelectionPopup = async (): Promise<void> => {
        await this.togglePopup('isSelectionTemplateOpen');
    };
}
